import { API_BASE_URL } from '../config/api';

const getAuthHeaders = (json = true) => {
  const token = localStorage.getItem('token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
};

// Parse response safely — handles HTML error pages from proxy failures
const parseResponse = async (res, fallbackMsg) => {
  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');

  if (!res.ok) {
    if (isJson) {
      try {
        const err = await res.json();
        throw new Error(err.error || fallbackMsg);
      } catch (e) {
        throw new Error(e.message || fallbackMsg);
      }
    }
    throw new Error(`${fallbackMsg} (HTTP ${res.status})`);
  }

  // Guard against proxy returning HTML with 200 status when backend is down
  if (!isJson) {
    throw new Error(fallbackMsg + ': server unreachable, please try again');
  }

  return res.json();
};

// ==================== PLACEMENTS ====================

export const getPlacements = async () => {
  const res = await fetch(`${API_BASE_URL}/ads/placements`, { headers: getAuthHeaders() });
  return parseResponse(res, 'Failed to fetch placements');
};

export const createPlacement = async (data) => {
  const res = await fetch(`${API_BASE_URL}/ads/placements`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return parseResponse(res, 'Failed to create placement');
};

export const updatePlacement = async (id, data) => {
  const res = await fetch(`${API_BASE_URL}/ads/placements/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  return parseResponse(res, 'Failed to update placement');
};

export const deletePlacement = async (id) => {
  const res = await fetch(`${API_BASE_URL}/ads/placements/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  return parseResponse(res, 'Failed to delete placement');
};

// ==================== CAMPAIGNS ====================

export const getCampaigns = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.placementId) params.set('placementId', filters.placementId);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await fetch(`${API_BASE_URL}/ads/campaigns${qs}`, { headers: getAuthHeaders() });
  return parseResponse(res, 'Failed to fetch campaigns');
};

export const getCampaign = async (id) => {
  const res = await fetch(`${API_BASE_URL}/ads/campaigns/${id}`, { headers: getAuthHeaders() });
  return parseResponse(res, 'Failed to fetch campaign');
};

export const createCampaign = async (formData) => {
  const res = await fetch(`${API_BASE_URL}/ads/campaigns`, {
    method: 'POST',
    headers: getAuthHeaders(false), // no Content-Type for multipart
    body: formData
  });
  return parseResponse(res, 'Failed to create campaign');
};

export const updateCampaign = async (id, formData) => {
  const res = await fetch(`${API_BASE_URL}/ads/campaigns/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(false),
    body: formData
  });
  return parseResponse(res, 'Failed to update campaign');
};

export const deleteCampaign = async (id) => {
  const res = await fetch(`${API_BASE_URL}/ads/campaigns/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  return parseResponse(res, 'Failed to delete campaign');
};

// ==================== STATS ====================

export const getStats = async () => {
  const res = await fetch(`${API_BASE_URL}/ads/stats`, { headers: getAuthHeaders() });
  return parseResponse(res, 'Failed to fetch stats');
};

// ==================== PUBLIC ====================

export const getPublicAds = async (placementSlug) => {
  const res = await fetch(`${API_BASE_URL}/ads/public/${placementSlug}`);
  if (!res.ok) return { ads: [] };
  return res.json();
};

export const trackAd = async (adId, placementSlug, type) => {
  fetch(`${API_BASE_URL}/ads/public/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adId, placementSlug, type })
  }).catch(() => {}); // fire-and-forget
};
